import { useState } from "react";
import toast from "react-hot-toast";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { governanceService } from "@/services/governance.service";
import type { PlatformUserRole } from "@/types";

const ROLE_OPTIONS: PlatformUserRole[] = [
  "SUPER_ADMIN",
  "SUPPORT_ADMIN",
  "SUPPORT_AGENT",
  "CHANNEL_PARTNER",
];

export function CreatePlatformUserModal({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [role, setRole] = useState<PlatformUserRole>("SUPPORT_AGENT");

  const { mutateAsync, isPending } = useMutation({
    mutationFn: () =>
      governanceService.createPlatformUser({
        email: email.trim(),
        password,
        firstName: firstName.trim(),
        lastName: lastName.trim() || undefined,
        role,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-platform-users"] });
      toast.success("Platform user created");
      onClose();
    },
    onError: (error: any) => {
      toast.error(error?.message || "Failed to create platform user");
    },
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 p-4">
      <div className="w-full max-w-lg rounded-3xl bg-white p-6 shadow-xl border border-slate-200">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-lg font-black text-slate-900">
              New platform user
            </h3>
            <p className="mt-1 text-xs text-slate-500 font-medium">
              Creates a login for the Admin Portal.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl border border-slate-200 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-600 hover:bg-slate-50"
          >
            Close
          </button>
        </div>

        <div className="mt-6 grid gap-3">
          <div className="grid gap-3 md:grid-cols-2">
            <input
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="First name"
              className="h-10 rounded-xl border border-slate-200 px-4 text-sm"
            />
            <input
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Last name (optional)"
              className="h-10 rounded-xl border border-slate-200 px-4 text-sm"
            />
          </div>
          <input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            className="h-10 rounded-xl border border-slate-200 px-4 text-sm"
          />
          <input
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Temporary password"
            type="password"
            className="h-10 rounded-xl border border-slate-200 px-4 text-sm"
          />
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as PlatformUserRole)}
            className="h-10 rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-700"
          >
            {ROLE_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>

          <button
            type="button"
            disabled={
              !email.trim() || !password || !firstName.trim() || isPending
            }
            onClick={() => mutateAsync()}
            className="mt-2 rounded-xl bg-slate-900 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-white disabled:cursor-not-allowed disabled:opacity-70"
          >
            {isPending ? "Creating..." : "Create user"}
          </button>
        </div>
      </div>
    </div>
  );
}
